import React, { useState, useEffect } from "react";
import { Container, Accordion, Modal } from "react-bootstrap";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import BackButton from "../../../../components/UI_Kit/BackButton/BackButton";
import api from "../../../../services/api";
import "./style.css";
import GoogleMapDisplay from "../../../../components/GoogleMapDisplay/GoogleMapDisplay"; // Adjust path if necessary

/**
 * Summary Component - Displays a summary of quotation points with pricing options
 *
 * Features:
 * - Shows detailed information about each point in an accordion
 * - Displays client and quotation information
 * - Provides pricing options (leasing/purchase)
 * - Includes interactive map for point locations
 */
const Summary = () => {
  // Navigation and route params
  const navigate = useNavigate();
  const { id } = useParams();

  // Pricing state - Modified for checkboxes
  const [isLeasingChecked, setIsLeasingChecked] = useState(false);
  const [isAchatChecked, setIsAchatChecked] = useState(false);
  const [monthlyRent, setMonthlyRent] = useState(0);
  const [soldPrice, setSoldPrice] = useState(0);
  const [monthlyRentTTC, setMonthlyRentTTC] = useState(0);
  const [totalPurchaseTTC, setTotalPurchaseTTC] = useState(0);
  const [selectedMonths, setSelectedMonths] = useState(1);
  const [showOpportunity, setShowOpportunity] = useState(false);
  const [opportunityAmount, setOpportunityAmount] = useState(0);
  const [validityDate, setValidityDate] = useState("");

  // Quotation data state
  const [points, setPoints] = useState([]);
  const [quotation, setQuotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Map modal state
  const [showMapModal, setShowMapModal] = useState(false);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedPointTitle, setSelectedPointTitle] = useState([]);

  // Add timeout effect to redirect to step2
  const [countdown, setCountdown] = useState(3); // Add this state for the timer

  useEffect(() => {
    if (points && points.length === 0) {
      setCountdown(3); // Reset timer on mount or when points change
      const interval = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
      const redirectTimer = setTimeout(() => {
        navigate(`/invoice/create/step2/${id}`);
      }, 3000);
      return () => {
        clearTimeout(redirectTimer);
        clearInterval(interval);
      };
    }
  }, [navigate, id, points]);

  // useEffect to calculate totalPurchaseTTC based on the provided formula
  useEffect(() => {
    if (isAchatChecked && quotation) {
      const numericSoldPrice = parseFloat(soldPrice) || 0;
      const fai_ht = parseFloat(quotation.fai) || 0;
      const annual_maintenance_ht =
        parseFloat(quotation.annual_maintenance) || 0;

      const remise = numericSoldPrice * 0.12;
      const baseForTVA =
        numericSoldPrice - remise + fai_ht + annual_maintenance_ht;
      const tvaAmount = baseForTVA * 0.2; // TVA à 20%
      const newTotalPurchaseTTC = baseForTVA + tvaAmount;

      setTotalPurchaseTTC(newTotalPurchaseTTC);
    } else {
      setTotalPurchaseTTC(0);
    }
  }, [soldPrice, quotation, isAchatChecked, setTotalPurchaseTTC]);

  // useEffect to calculate monthlyRentTTC based on the provided formula
  useEffect(() => {
    if (isLeasingChecked && quotation && selectedMonths > 0) {
      const numericMonthlyRent = parseFloat(monthlyRent) || 0;
      const fai_ht = parseFloat(quotation.fai) || 0;

      const remise = numericMonthlyRent * 0.12; // Remise de 12% sur le loyer HT mensuel vendu
      const faiPerMonth = fai_ht / selectedMonths;

      const baseForTVA = numericMonthlyRent - remise + faiPerMonth;
      const tvaAmount = baseForTVA * 0.2; // TVA à 20%
      const newMonthlyRentTTC = baseForTVA + tvaAmount;

      setMonthlyRentTTC(newMonthlyRentTTC);
    } else {
      setMonthlyRentTTC(0); // Réinitialiser si leasing n'est pas coché, quotation non chargé, ou selectedMonths est 0
    }
  }, [
    monthlyRent,
    quotation,
    selectedMonths,
    isLeasingChecked,
    setMonthlyRentTTC,
  ]);

  const handleValidate = async () => {

    let data = {
      "leasing": isLeasingChecked,
      "achat": isAchatChecked,
      "rental_months": selectedMonths,
      "rent_ht_monthly_sold": monthlyRent,
      "is_oportunity_proposed": showOpportunity,
      "purchase_price_ht_sold": showOpportunity ? opportunityAmount : soldPrice,
      "oportunity_expire_at": validityDate
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("access_token");
      const response = await api.post(`/quotation-ask-for-validation/${id}`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success(response?.message);
      navigate("/");
    } catch (err) {
      setError("Failed to validate quotation");
      toast.error("Failed to validate quotation");
    } finally {
      setLoading(false);
    }
  };

  const handleDraft = async () => {
    // TODO: Implement draft saving logic
    let data = {
      "leasing": isLeasingChecked,
      "achat": isAchatChecked,
      "rental_months": selectedMonths,
      "rent_ht_monthly_sold": monthlyRent,
      "is_oportunity_proposed": showOpportunity,
      "purchase_price_ht_sold": showOpportunity ? opportunityAmount : soldPrice,
      "oportunity_expire_at": validityDate,
      "status": "draft"
    }
    try {
      setLoading(true);
      const token = localStorage.getItem("access_token");
      const response = await api.patch(`/quotation/${id}`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success(response?.message);
      navigate("/");
    } catch (err) {
      setError("Failed to validate quotation");
      toast.error("Failed to validate quotation");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Opens map modal with locations for a specific point
   * @param {Object} point - The point containing locations to display
   */
  const handleOpenMapModal = (point) => {
    if (point.localisations && point.localisations.length > 0) {
      setSelectedLocations(point.localisations);
      setSelectedPointTitle([point.full_type, point.title]);
      setShowMapModal(true);
    } else {
      toast.info("Aucune localisation disponible pour ce point");
    }
  };

  const handleCloseMapModal = () => {
    setShowMapModal(false);
    setSelectedLocations([]);
    setSelectedPointTitle("");
  };

  useEffect(() => {
    const fetchQuotation = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("access_token");
        const response = await api.get(`/quotation/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const fetchedQuotation = response.data.quotation;
        setQuotation(fetchedQuotation);

        const pricingMap = {
          purchase: { leasing: false, achat: true },
          leasing: { leasing: true, achat: false },
          purchase_and_leasing: { leasing: true, achat: true },
        };

        const { leasing = false, achat = false } = pricingMap[fetchedQuotation?.pricing] || {};

        setIsLeasingChecked(leasing);
        setIsAchatChecked(achat);
        setSelectedMonths(fetchedQuotation?.rental_months)
        setMonthlyRent(fetchedQuotation?.rent_ht_monthly_sold)
        setShowOpportunity(fetchedQuotation?.is_oportunity_proposed)
        setOpportunityAmount(fetchedQuotation?.purchase_price_ht_sold)
        setValidityDate(fetchedQuotation?.oportunity_expire_at)
        setSoldPrice(fetchedQuotation?.purchase_price_ht_sold)


        setPoints(response.data.quotation.points || []);
      } catch (err) {
        setError("Failed to fetch quotation data");
        toast.error("Failed to fetch quotation data");
        console.error("Error fetching quotation:", err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchQuotation();
    } else {
      setError("Quotation ID is missing.");
      setLoading(false);
    }
  }, [id]);

  // Function to handle editing a point
  const handleEditPoint = (index) => {
    const pointToEdit = points[index];
    const apiPointType = pointToEdit.type || pointToEdit.pointType;
    const dataForEdit = {
      id: pointToEdit.id,
      title: pointToEdit.title || pointToEdit.pointName,
      type: apiPointType,
      support_type: pointToEdit.support_type || pointToEdit.supportType,
      connection_type:
        pointToEdit.connection_type || pointToEdit.installationType,
      power_type: pointToEdit.power_type || pointToEdit.powerSupplyType,
      comment: pointToEdit.comment || "",
      has_camera: pointToEdit.has_camera,
      cameras: pointToEdit.cameras || [],
      localisations: pointToEdit.localisations || [],
      recorder_size: pointToEdit.recorder_size || pointToEdit.recorderSize,
      screen_size: pointToEdit.screen_size || pointToEdit.screenSize,
      coverage_area: pointToEdit.coverage_area || pointToEdit.coverageArea,
    };
    navigate("/invoice/edit-point", {
      state: {
        pointToEdit: dataForEdit,
        quotationId: quotation?.id,
      },
    });
  };

  // Helper function for capitalizing status (if not already available globally)
  const capitalizeFirstLetter = (string) => {
    if (!string) return "";
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  return (
    <HelmetProvider>
      <ToastContainer />
      <Helmet>
        <title>Récapitulatif des Points</title>
        <meta name="description" content="Récapitulatif des points ajoutés" />
      </Helmet>

      <Container className="pt-4 d-flex justify-content-center ">
        <div className="container-wrap w-100">
          <div className="header  d-flex align-items-center">
            <BackButton className="back-button" />
            <span className="step-title">Récapitulatif des Points</span>
          </div>
          {/* ========== STEP PROGRESS INDICATOR ========== */}
          <div className="step_bar d-flex justify-content-between align-items-center w-100">
            <img
              className="step-image img-fluid"
              src="/step_done.png"
              alt="step1"
            />
            <img
              className="step-image img-fluid"
              src="/step_done.png"
              alt="step2"
            />
            <img
              className="step-image img-fluid"
              src="/step_done.png"
              alt="step3"
            />
          </div>
          {loading ? (
            <>
              <div className="container">
                <Skeleton height={30} style={{ marginBottom: "1rem" }} />
                <Skeleton height={20} style={{ marginBottom: "3rem" }} />
                <Skeleton
                  height={100}
                  count={1}
                  style={{ marginBottom: "1rem" }}
                />
                <Skeleton
                  height={100}
                  count={3}
                  style={{ marginBottom: "1rem" }}
                />
              </div>
            </>
          ) : (
            <>
              {quotation && (
                <div className="quotation-info mb-2">
                  <h3 className="quotation-number">
                    DEVIS N° {quotation.quotation_number}
                  </h3>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="date-time">
                      Date :{" "}
                      {new Date(quotation.created_at).toLocaleDateString(
                        "fr-FR",
                        {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        }
                      )}
                    </span>
                    <div
                      className={`status-badge-custom  status-${capitalizeFirstLetter(
                        quotation.status
                      )}`}
                    >
                      <div className="status-content">
                        <span className={`status-badge-dot`}></span>
                        <span className="status-text">
                          {capitalizeFirstLetter(quotation.translated_status)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {points.length === 0 ? (
                    <div className="alert alert-info">
                      Aucun point n'a été ajouté à ce devis.<br></br>
                      <small>Redirection dans {countdown}...</small>
                    </div>
                  ) : (
                    <>
                      <h4 className="invoice-subtitle">Type de point</h4>
                      <Accordion
                        defaultActiveKey="null"
                        className="points-accordion"
                      >
                        {points.map((point, index) => (
                          <Accordion.Item
                            key={index}
                            eventKey={index.toString()}
                            className="mb-3"
                          >
                            <Accordion.Header>
                              <div>
                                <div className="header-title">
                                  {point.full_type || point.pointType}
                                </div>
                                <div className="header-subtitle">
                                  {point.title || point.pointName}
                                </div>
                              </div>
                            </Accordion.Header>
                            <Accordion.Body>
                              {/* Common fields for all point types */}
                              <div className="point-detail mb-3">
                                <div className="header-title">
                                  Type de support:
                                </div>
                                <div className="header-subtitle">
                                  {point.support_type || point.supportType}
                                </div>
                              </div>
                              <div className="point-detail mb-3">
                                <div className="header-title">
                                  Type de liaison:
                                </div>
                                <div className="header-subtitle">
                                  {point.connection_type ||
                                    point.installationType}
                                </div>
                              </div>
                              <div className="point-detail mb-3">
                                <div className="header-title">
                                  Type d'alimentation:
                                </div>
                                <div className="header-subtitle">
                                  {point.power_type || point.powerSupplyType}
                                </div>
                              </div>

                              {/* Type-specific fields */}

                              {(point.type === "surveillance_center" ||
                                point.pointType === "surveillance_center") && (
                                  <div className="point-detail mb-3">
                                    <div className="header-title">
                                      Taille de l'enregistreur:
                                    </div>
                                    <div className="header-subtitle">
                                      {point.recorder_size || point.recorderSize}
                                    </div>
                                  </div>
                                )}

                              {(point.type === "surveillance_center" ||
                                point.pointType === "surveillance_center") && (
                                  <div className="point-detail mb-3">
                                    <div className="header-title">
                                      Taille de l'écran:
                                    </div>
                                    <div className="header-subtitle">
                                      {point.screen_size || point.screenSize}
                                    </div>
                                  </div>
                                )}

                              {(point.type === "high_point" ||
                                point.pointType === "high_point") && (
                                  <div className="point-detail mb-3">
                                    <div className="header-title">
                                      Zone de couverture:
                                    </div>
                                    <div className="header-subtitle">
                                      {point.coverage_area || point.coverageArea}
                                    </div>
                                  </div>
                                )}

                              {/* Comment field for all types */}
                              {point.comment && (
                                <div className="point-detail mb-3">
                                  <div className="header-title">
                                    Commentaire:
                                  </div>
                                  <div className="header-subtitle">
                                    {point.comment}
                                  </div>
                                </div>
                              )}

                              {/* Modified Locations/photos section */}
                              {point.localisations &&
                                point.localisations.length > 0 && (
                                  <div className="localisations mt-3">
                                    <a
                                      href="#OpenMaps"
                                      className="SeeAllLocatiion"
                                      onClick={() => handleOpenMapModal(point)}
                                    >
                                      <img
                                        src="/map_location.png"
                                        alt="Map Location"
                                        style={{
                                          width: "24px",
                                          marginRight: "8px",
                                        }}
                                      />
                                      Voir les localisations
                                    </a>
                                  </div>
                                )}

                              <a
                                href="#OpenEdits"
                                onClick={() => handleEditPoint(index)}
                                className="mt-4 EditPoint"
                              >
                                <img
                                  src="/edit_alt.png"
                                  alt="Edit"
                                  style={{ width: "24px", marginRight: "8px" }}
                                />
                                Modifier le point
                              </a>
                            </Accordion.Body>
                          </Accordion.Item>
                        ))}
                      </Accordion>

                      <div className="tarification-section mt-3">
                        <h4 className="invoice-subtitle">Tarification</h4>
                        <div className="d-flex mb-3">
                          <div className="checkbox-label custom-checkbox me-4">
                            <input
                              type="checkbox"
                              id="leasing"
                              checked={isLeasingChecked}
                              onChange={(e) =>
                                setIsLeasingChecked(e.target.checked)
                              }
                            />
                            <label className="checkbox-label" htmlFor="leasing">
                              Leasing
                            </label>
                          </div>
                          <div className="checkbox-label custom-checkbox me-4">
                            <input
                              type="checkbox"
                              id="achat"
                              checked={isAchatChecked}
                              onChange={(e) =>
                                setIsAchatChecked(e.target.checked)
                              }
                            />
                            <label className="checkbox-label" htmlFor="achat">
                              Achat
                            </label>
                          </div>
                        </div>

                        {/* Leasing Fields - Show if leasing is checked */}
                        {isLeasingChecked && (
                          <div>
                            <div className="mb-2 d-flex justify-content-between">
                              <span className="tarification-small-title">
                                Loyer total HT
                              </span>
                              <span className="tarification-small-title">
                                {selectedMonths *
                                  quotation.total_products_rental}{" "}
                                €
                              </span>
                            </div>

                            <div className="mb-4">
                              <span className="monthly-rent-container">
                                <span className="tarification-bold-title monthly-rent-label">
                                  Loyer HT mensuel vendu
                                </span>
                                <div className="monthly-rent-input-group d-flex align-items-center gap-2">
                                  <div style={{ width: "200px" }}>
                                    <select
                                      id="selectedMonthsInput"
                                      className="form-select tarification-small-title"
                                      value={selectedMonths}
                                      onChange={(e) =>
                                        setSelectedMonths(
                                          parseInt(e.target.value, 10)
                                        )
                                      }
                                    >
                                      {[...Array(12).keys()].map((i) => (
                                        <option key={i + 1} value={i + 1}>
                                          {i + 1} mois
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  <input
                                    type="text" // type="number"
                                    className="tarification-small-title form-control monthly-rent-input"
                                    value={monthlyRent}
                                    onChange={(e) =>
                                      setMonthlyRent(e.target.value)
                                    }
                                  />
                                  <span className="tarification-small-title monthly-rent-suffix">
                                    €/mois
                                  </span>
                                </div>
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Opportunity Section - Only if Leasing is checked AND Achat is NOT checked */}
                        {isLeasingChecked && !isAchatChecked && (
                          <>
                            <div className="checkbox-label custom-checkbox  mb-4">
                              <input
                                type="checkbox"
                                id="opportunite"
                                checked={showOpportunity}
                                onChange={(e) =>
                                  setShowOpportunity(e.target.checked)
                                }
                              />
                              <label
                                className="checkbox-label"
                                htmlFor="opportunite"
                              >
                                Opportunité proposée
                              </label>
                            </div>

                            {showOpportunity && (
                              <>
                                <div className="mb-3">
                                  <label
                                    htmlFor="opportunityAmount"
                                    className="form-label"
                                  >
                                    Montant vendu avec opportunité
                                  </label>
                                  <div className="monthly-rent-input-group d-flex align-items-center gap-2">
                                    <input
                                      type="text" // type="number"
                                      className="form-control tarification-small-title monthly-rent-input"
                                      id="opportunityAmount"
                                      value={opportunityAmount}
                                      onChange={(e) =>
                                        setOpportunityAmount(e.target.value)
                                      }
                                    />
                                    <span className="tarification-small-title monthly-rent-suffix">
                                      €/mois
                                    </span>
                                  </div>
                                </div>

                                <div className="mb-3">
                                  <label
                                    htmlFor="validityDate"
                                    className="form-label"
                                  >
                                    Date de validité
                                  </label>
                                  <div className="monthly-rent-input-group d-flex align-items-center gap-2">
                                    <input
                                      type="date"
                                      className="form-control tarification-small-title monthly-rent-input"
                                      id="validityDate"
                                      value={validityDate}
                                      onChange={(e) =>
                                        setValidityDate(e.target.value)
                                      }
                                    />

                                    <span className="tarification-small-title monthly-rent-suffix">
                                      <img
                                        src="/calendar.png"
                                        style={{ width: "24px" }}
                                        alt="calendar"
                                      ></img>
                                    </span>
                                  </div>
                                </div>
                              </>
                            )}
                          </>
                        )}

                        {/* Achat Fields - Show if achat is checked */}
                        {isAchatChecked && (
                          <div className="mb-4">
                            <div className="mb-2 d-flex justify-content-between">
                              <span className="tarification-small-title">
                                Prix d'achat total HT
                              </span>
                              <span className="tarification-small-title">
                                {quotation?.total_products_sale || "0.00"} €{" "}
                              </span>
                            </div>
                            <div className="mb-4">
                              <span className="monthly-rent-container">
                                <span className="tarification-bold-title monthly-rent-label">
                                  Prix d’achat HT vendu
                                </span>
                                <div className="monthly-rent-input-group d-flex align-items-center gap-2">
                                  <input
                                    type="text"
                                    className="form-control tarification-small-title monthly-rent-input"
                                    value={soldPrice}
                                    onChange={(e) =>
                                      setSoldPrice(e.target.value)
                                    }
                                  />
                                  <span className="tarification-small-title monthly-rent-suffix">
                                    €
                                  </span>
                                </div>
                              </span>
                            </div>
                          </div>
                        )}

                        {(isLeasingChecked || isAchatChecked) && (
                          <>
                            {/* Common fields */}
                            <div className="mt-3 mb-4 d-flex justify-content-between">
                              <span className="tarification-small-title">
                                Remise
                              </span>
                              <span className="tarification-small-title">
                                {quotation.discount} %
                              </span>
                            </div>
                            <div className="mb-4 d-flex justify-content-between">
                              <span className="tarification-small-title">
                                FAI HT
                              </span>
                              <span className="tarification-small-title">
                                {quotation.fai} €
                              </span>
                            </div>
                            {isAchatChecked && !isLeasingChecked && (
                              <div className="mb-4 d-flex justify-content-between">
                                <span className="tarification-small-title">
                                  Maintenance annuelle
                                </span>
                                <span className="tarification-small-title">
                                  {quotation.annual_maintenance} €
                                </span>
                              </div>
                            )}
                            <div className="mb-4 d-flex justify-content-between">
                              <span className="tarification-small-title">
                                TVA
                              </span>
                              <span className="tarification-small-title">
                                {quotation.vat} %
                              </span>
                            </div>

                            <hr />
                          </>
                        )}

                        {/* TTC Fields - Display based on checkboxes */}
                        {isLeasingChecked && (
                          <div className="mb-4">
                            <div className="d-flex justify-content-between">
                              <span className="tarification-bold-title">
                                Loyer mensuel TTC
                              </span>
                              <span className="tarification-bold-title">
                                {monthlyRentTTC > 0
                                  ? `${monthlyRentTTC.toFixed(2)} €`
                                  : "-"}
                              </span>
                            </div>
                            <small className="text-muted">
                              Maintenance annuelle comprise
                            </small>
                          </div>
                        )}

                        <div className="mb-4">
                          {isAchatChecked && (
                            <div className=" d-flex justify-content-between">
                              <span className="tarification-bold-title">
                                Prix d’achat total TTC
                              </span>

                              <span className="tarification-bold-title">
                                {totalPurchaseTTC > 0
                                  ? `${totalPurchaseTTC.toFixed(2)} €`
                                  : "-"}
                              </span>
                            </div>
                          )}
                          {isAchatChecked && isLeasingChecked && (
                            <div className="mt-1 d-flex justify-content-between">
                              <small className="text-muted">
                                Maintenance annuelle
                              </small>

                              <span className="text-muted">
                                {quotation.annual_maintenance} €
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          )}

          {/* ========== FOOTER SECTION ========== */}
          
          {loading ? (
            <>
              <div className="container">
                <Skeleton height={30} style={{ marginBottom: "1rem" }} />
              </div>
            </>
          ) : (

          <div className="footer shadow-lg d-flex justify-content-center">
            <button
              className="primary-button w-100 mb-2"
              onClick={handleValidate}
            >
              Demander une validation
            </button>
            <button className="secondary-button w-100" onClick={handleDraft}>
              Mettre en brouillon
            </button>
          </div>
          )}
          

          {error && <div className="alert alert-danger">{error}</div>}
        </div>
      </Container>

      {/* Google Maps Modal */}
      <Modal
        show={showMapModal}
        onHide={handleCloseMapModal}
        size="xl"
        centered
        dialogClassName="map-modal-full"
        contentClassName="map-modal-content"
      >
        <div className="map-container">
          {/* Using BackButton component */}
          <div className="map-header">
            <div className="map-header-content">
              <BackButton
                onClick={handleCloseMapModal}
                className="map-back-button"
              />
              <div className="map-title-container mt-3">
                <h5 className="map-title">{selectedPointTitle[0]}</h5>
                <p className="map-subtitle">{selectedPointTitle[1]}</p>
              </div>
            </div>
          </div>

          {/* Full-width map */}
          <GoogleMapDisplay locations={selectedLocations} />

          {/* Floating location counter */}
          <div className="map-location-counter">
            <span>{selectedLocations.length} locations</span>
          </div>
        </div>
      </Modal>
    </HelmetProvider>
  );
};

export default Summary;
